import React, {
  FunctionComponent,
  KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Circle360 from './styling/360.svg';
import { DragStartEvent, useDragHandlers } from './helpers/dragHandlers';
import { loadImage } from './helpers/imageLoader';
import {
  ThreeSixtyExteriorViewContainer,
  ThreeSixtyExteriorViewFocusHelper,
  ThreeSixtyExteriorViewStyledImage,
  ThreeSixtyExteriorViewEllipse,
  ThreeSixtyViewControlsWrapper,
  ThreeSixtyExteriorViewSpinnerWrapper,
  ThreeSixtyViewerFocusElement,
} from './styling/exteriorThreeSixtyStyling';
import { Spinner } from '../../spinner/Spinner';

interface ThreeSixtyExteriorViewProps {
  readonly assetUrls: string[];
}

const preloadImages = async (images: string[]): Promise<void> => {
  await Promise.all(images.map((src) => loadImage(src)));
};

export const ExteriorThreeSixtyView: FunctionComponent<ThreeSixtyExteriorViewProps> = ({
  assetUrls,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);
  const [shouldShowSpinner, setShouldShowSpinner] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsWrapperRef = useRef<HTMLDivElement>(null);
  const loadingThresholdHandle = useRef<number>();
  const startIndex = useRef<number | undefined>();

  const loadingThreshold = 150;

  const handleKeyPress = (e: ReactKeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      setCurrentImageIndex((idx) => (idx + 1) % assetUrls.length);
    }

    if (e.key === 'ArrowLeft') {
      setCurrentImageIndex((idx) => (idx === 0 ? assetUrls.length - 1 : idx - 1));
    }
  };

  const onDragStart = () => {
    startIndex.current = currentImageIndex;
  };

  const updateImage = useCallback(
    (clientX: number, dragStart: DragStartEvent) => {
      const speed = 1;
      const deltaX = dragStart.x - clientX;
      const ratio = (360 + ((deltaX * speed) % 360)) / 360;
      const index = Math.round(ratio * assetUrls.length) % assetUrls.length;

      setCurrentImageIndex(((startIndex.current ?? 0) + index) % assetUrls.length);
    },
    [assetUrls.length],
  );

  const handleDragStart = useDragHandlers(updateImage, onDragStart, undefined, shouldShowSpinner);

  useEffect(() => {
    const container = controlsWrapperRef.current;

    if (container) {
      container.addEventListener('touchstart', handleDragStart, {
        passive: false,
      });
    }

    return () => {
      if (container) {
        container.removeEventListener('touchstart', handleDragStart, false);
      }
    };
  }, [handleDragStart]);

  useEffect(() => {
    const getImages = async () => {
      await preloadImages(assetUrls).then(() => {
        clearTimeout(loadingThresholdHandle.current);
        setShouldShowSpinner(false);
      });
    };
    if (assetUrls.length) {
      loadingThresholdHandle.current = window.setTimeout(
        () => setShouldShowSpinner(true),
        loadingThreshold,
      );

      getImages();
    }
  }, [assetUrls]);

  useEffect(
    () => () => {
      clearTimeout(loadingThresholdHandle.current);
    },
    [],
  );

  return (
    <ThreeSixtyExteriorViewContainer ref={containerRef} data-testid="turntable">
      <ThreeSixtyExteriorViewFocusHelper
        onKeyDown={handleKeyPress}
        data-testid="three-sixty-focus-helper"
      />
      {assetUrls.map((url, index) => (
        <ThreeSixtyExteriorViewStyledImage
          key={url}
          src={url}
          isVisible={index === currentImageIndex}
          data-testid={`exterior-360-image-${index}`}
          aria-label={`exterior-360-image-${index}`}
        />
      ))}
      <ThreeSixtyExteriorViewEllipse svgImage={Circle360} data-testid="three-sixty-image" />
      <ThreeSixtyViewControlsWrapper
        ref={controlsWrapperRef}
        onMouseDown={handleDragStart}
        data-testid="three-sixty-controls-wrapper"
      >
        {shouldShowSpinner && (
          <ThreeSixtyExteriorViewSpinnerWrapper data-testid="three-sixty-loading-spinner">
            <Spinner aria-hidden="true" />
          </ThreeSixtyExteriorViewSpinnerWrapper>
        )}
      </ThreeSixtyViewControlsWrapper>
      <ThreeSixtyViewerFocusElement />
    </ThreeSixtyExteriorViewContainer>
  );
};
